import { Navigate, Route, Routes } from 'react-router';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth, RequirePermission } from '@/components/layout/Guards';
import { useAuth } from '@/lib/auth';
import { homePath } from '@/lib/permissions';
import { LoginPage } from '@/pages/Login';
import { AcceptInvitePage } from '@/pages/AcceptInvite';
import { DashboardPage } from '@/pages/Dashboard';
import { RequirementsPage } from '@/pages/Requirements';
import { OrganizationsPage } from '@/pages/Organizations';
import { MembersPage } from '@/pages/Members';
import { ProjectsPage } from '@/pages/Projects';
import { ProjectDetailPage } from '@/pages/ProjectDetail';
import { TestBuilderPage } from '@/pages/TestBuilder';
import { MobileRecordPage } from '@/pages/MobileRecord';
import { RunReportPage } from '@/pages/RunReport';
import { ReportsPage } from '@/pages/Reports';
import { SharedReportPage } from '@/pages/SharedReport';
import { AdminUsersPage } from '@/pages/admin/Users';
import { AdminInvitationsPage } from '@/pages/admin/Invitations';
import { PluginsPage } from '@/pages/Plugins';
import { AdminAuditPage } from '@/pages/admin/Audit';
import { AdminOrganizationsPage } from '@/pages/admin/Organizations';
import { AiModelsPage } from '@/pages/AiModels';
import { OrgTestDataPage } from '@/pages/OrgTestData';
import { FlowEditorPage } from '@/pages/FlowEditor';
import { FlowRunPage } from '@/pages/FlowRun';
import { RecorderPage } from '@/pages/Recorder';
import { NotFoundPage } from '@/pages/NotFound';
import { GuidePage } from '@/pages/Guide';
import { DevicesPage } from '@/pages/Devices';
import { HelperRedirect, HelperServicesLayout, HelperServicesPage } from '@/pages/HelperServices';
import { LoadAgentsPage } from '@/pages/LoadAgents';
import { PerfImportPage } from '@/pages/PerfImport';
import { PerfScriptPage } from '@/pages/PerfScript';
import { PerfTestPage } from '@/pages/PerfTest';
import { SecurityTestPage } from '@/pages/SecurityTest';

function Home() {
  const { user, currentOrgRole } = useAuth();
  const target = homePath(user?.platformRole ?? 'USER', currentOrgRole);
  return target === '/' ? <DashboardPage /> : <Navigate to={target} replace />;
}

export function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route path="/shared/:token" element={<SharedReportPage />} />

      {/* Authenticated */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="runs/:runId" element={<RunReportPage />} />
        <Route path="flow-runs/:flowRunId" element={<FlowRunPage />} />
        <Route path="orgs" element={<OrganizationsPage />} />
        <Route
          path="members"
          element={
            <RequirePermission org="org.members.read">
              <MembersPage />
            </RequirePermission>
          }
        />
        <Route
          path="projects"
          element={
            <RequirePermission org="project.read">
              <ProjectsPage />
            </RequirePermission>
          }
        />
        <Route
          path="test-data"
          element={
            <RequirePermission org="test.read">
              <OrgTestDataPage />
            </RequirePermission>
          }
        />
        <Route path="orgs/:orgId/test-data" element={<OrgTestDataPage />} />
        {/* Helper services (features/helpers/services.ts): one page, a sub-page per service. */}
        <Route path="helpers" element={<HelperServicesLayout />}>
          <Route index element={<HelperServicesPage />} />
          <Route path="recorder" element={<RecorderPage />} />
          <Route path="devices" element={<DevicesPage />} />
          <Route path="load-agents" element={<LoadAgentsPage />} />
        </Route>
        <Route path="recorder" element={<HelperRedirect to="recorder" />} />
        <Route path="devices" element={<HelperRedirect to="devices" />} />
        <Route path="guide" element={<GuidePage />} />
        <Route
          path="ai-models"
          element={
            <RequirePermission org="llm.manage">
              <AiModelsPage />
            </RequirePermission>
          }
        />
        <Route
          path="plugins"
          element={
            <RequirePermission org="plugins.manage">
              <PluginsPage />
            </RequirePermission>
          }
        />
        <Route path="projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="projects/:projectId/tests/new" element={<TestBuilderPage />} />
        <Route path="projects/:projectId/tests/record" element={<MobileRecordPage />} />
        <Route path="projects/:projectId/requirements" element={<RequirementsPage />} />
        <Route path="projects/:projectId/requirements/:setId" element={<RequirementsPage />} />
        <Route path="projects/:projectId/tests/:testId" element={<TestBuilderPage />} />
        <Route path="projects/:projectId/perf/new" element={<PerfTestPage />} />
        <Route path="projects/:projectId/perf/scripts/new" element={<PerfImportPage />} />
        <Route path="projects/:projectId/perf/scripts/:scriptId" element={<PerfScriptPage />} />
        <Route path="projects/:projectId/perf/:testId" element={<PerfTestPage />} />
        <Route path="projects/:projectId/security/new" element={<SecurityTestPage />} />
        <Route path="projects/:projectId/security/:testId" element={<SecurityTestPage />} />
        <Route path="projects/:projectId/flows/:flowId" element={<FlowEditorPage />} />

        <Route path="admin">
          <Route
            path="users"
            element={
              <RequirePermission platform="platform.users.manage">
                <AdminUsersPage />
              </RequirePermission>
            }
          />
          <Route
            path="invitations"
            element={
              <RequirePermission platform="platform.invite.projectAdmin">
                <AdminInvitationsPage />
              </RequirePermission>
            }
          />
          <Route
            path="organizations"
            element={
              <RequirePermission platform="platform.orgs.readAll">
                <AdminOrganizationsPage />
              </RequirePermission>
            }
          />
          {/* Moved to Project Admins in v0.4 */}
          <Route path="ai-models" element={<Navigate to="/ai-models" replace />} />
          <Route path="plugins" element={<Navigate to="/plugins" replace />} />
          <Route
            path="audit"
            element={
              <RequirePermission platform="platform.audit.read">
                <AdminAuditPage />
              </RequirePermission>
            }
          />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
